import React, { FC, useState, useCallback } from "react";
import {
  Drawer,
  Typography,
  Button,
  ScrollContainer,
  ShoppingBagItem,
  IShoppingBagItem,
  Divider,
  ThemeProvider,
  TSelectorData,
  TSelectorOption,
} from "../../components";

import { CheckIcon } from "../../icons";

export interface IUpsellFlyout {
  addedItems?: IShoppingBagItem[];
  upsellItems?: IShoppingBagItem[];
  sizePickerData?: TSelectorData;
  onSizeSelected?(item: TSelectorOption): void;
  onClickCheckout?(): void;
  labels?: {
    addedToCart?: string;
    submitButtonMobileLabel?: string;
    upsellSectionTitle?: string;
  };
}

function RenderButton({ onClick }) {
  return (
    <Button variant="primary" size="small" type="button" onClick={onClick}>
      Open Upsell Flyout
    </Button>
  );
}

const UpsellFlyout: FC<IUpsellFlyout> = ({
  addedItems,
  upsellItems,
  onSizeSelected,
  onClickCheckout,
  sizePickerData,
  labels,
}) => {
  const {
    addedToCart = "Added to cart",
    submitButtonMobileLabel = "Checkout",
    upsellSectionTitle = "People Also Bought",
  } = labels;

  const [newUpsellItems, setNewUpsellItems] = useState(upsellItems);
  const [openItemId, setOpenItemId] = useState<string>(null);
  const [open, setOpen] = useState(false);
  const [sizeOpen, setSizeOpen] = useState(false);
  const [useClickOutsideWrapper, setUseClickOutsideWrapper] = useState(true);

  const handleClickCheckoutButton = () => {
    onClickCheckout?.();
  };

  const onOpenDrawer = () => {
    setOpen(!open);
  };

  const handleAddToCart = (id: string) => {
    // Open size selector and disable onclickoutside
    setUseClickOutsideWrapper(false);
    setSizeOpen(true);
    setOpenItemId(id);
  };

  const handleSizeSelected = useCallback(
    (sizeItem: TSelectorOption) => {
      // Use it to add item to cart
      onSizeSelected?.(sizeItem);

      // Update the checkmark on the upsell item
      const newList = newUpsellItems.map((upsellItem) => {
        if (upsellItem.id === openItemId) {
          const updatedItem = {
            ...upsellItem,
            isAddedToCart: true,
          };

          return updatedItem;
        }

        return upsellItem;
      });

      setNewUpsellItems(newList);

      setSizeOpen(false);
    },
    [openItemId, onSizeSelected, newUpsellItems],
  );

  const headerComponent = (
    <Typography
      classes={{ root: "d-flex align-items-center" }}
      variant="h-menu-small-bold"
      as="div"
    >
      <CheckIcon className="me-4" />
      <div>{addedToCart}</div>
    </Typography>
  );

  return (
    <ThemeProvider theme="dark">
      <RenderButton onClick={onOpenDrawer} />

      <Drawer
        opened={open}
        onClose={() => {
          setOpen(false);
        }}
        headerComponent={headerComponent}
        useClickOutside={useClickOutsideWrapper}
        classes={{
          root: "upsell-flyout",
          body: "justify-content-between",
        }}
        mask={!sizeOpen}
      >
        <ScrollContainer height="100%">
          <div className="mobile-menu pt-0">
            <ul className="shopping-bag--product-cards bg-color-blue mb-9">
              {addedItems?.map((data) => (
                <li key={data.id}>
                  <ShoppingBagItem {...data} type="mini" variant="dark" />
                  <Divider color="white" />
                </li>
              ))}
            </ul>
            <Typography variant="mobile-header-20-bold">
              {upsellSectionTitle}
            </Typography>
            <ul className="shopping-bag--product-cards mt-4">
              {newUpsellItems?.map((data) => (
                <li key={data.id}>
                  <ShoppingBagItem
                    {...data}
                    type="look"
                    variant="dark"
                    handleAddToCart={() => handleAddToCart(data.id)}
                    isAddedToCart={data.isAddedToCart}
                  />
                  <Divider color="white" />
                </li>
              ))}
            </ul>
          </div>
        </ScrollContainer>

        <div className="filter-mobile-footer bg-color-dark-blue flex-column pt-6">
          <Button
            type="submit"
            variant="conversion"
            onClick={handleClickCheckoutButton}
            classes={{ root: "w-100" }}
          >
            {submitButtonMobileLabel}
          </Button>
        </div>
      </Drawer>
      <Drawer
        opened={sizeOpen}
        onClose={() => {
          setSizeOpen(false);
          setUseClickOutsideWrapper(true);
        }}
        header="Select Size"
        customStyle={{ height: "300px", bottom: 0, top: "unset", zIndex: 8000 }}
        maskCustomStyle={{ zIndex: 7000 }}
      >
        <ScrollContainer height="100%">
          <div className="py-4 px-4">
            {sizePickerData?.items?.map((item) => (
              <div
                key={item.id}
                className="dropdown-option dropdown-option--selector-option h-menu-medium"
                onClick={() => handleSizeSelected(item)}
              >
                <div className="dropdown-option--notification">
                  {item.title}
                </div>
                <div className="dropdown-option__icon ms-3">
                  {item.subtitle}
                </div>
              </div>
            ))}
          </div>
        </ScrollContainer>
      </Drawer>
    </ThemeProvider>
  );
};

export default UpsellFlyout;
